import { useEffect, useState, type FormEvent } from 'react'

import { ApiError, api, can, refusalReason, type RiskSource } from '../lib/api'
import { Status, Toggletip } from '../components/kit'
import { ReasonTip } from '../components/vendor/ReasonTip'
import '../styles/sweep.css'

// Where risks come in from.
//
// The mirror of Send risks onward. That screen names the registers a copy of
// our vendor risks is pushed to; this one names the registers that push their
// risks to us, so a company already keeping its enterprise risks somewhere
// else reads one matrix instead of two.
//
// # WHY THE SCALE MAPPING IS ON THIS SCREEN AND NOT HIDDEN
//
// Their matrix is not ours. Zoho ships a 5x5 with its own words, ServiceNow
// ranks impact with 1 as the worst, a spreadsheet sends High and Low and no
// numbers at all. A risk whose level we have no mapping for is kept, shown,
// and left off the matrix, which is the only honest thing to do: dropping it
// would make the estate look smaller than it is, and guessing a middle value
// would put somebody's critical risk in the middle of a board's grid. So the
// number of unplaced risks is on the card, loudly, next to the way to fix it.

function describe(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.issues.length > 0) return err.issues.map((i) => i.detail).join(' ')
    return err.message + (err.sentRemedy ? ' ' + err.sentRemedy : '')
  }
  return err instanceof Error ? err.message : String(err)
}

const KIND_WORD: Record<string, string> = {
  FOCTTA_CORE: 'FOCTTA Core',
  GENERIC_WEBHOOK: 'Another risk register',
}

// A mapping is edited as text because that is how somebody holds it in their
// head: one level per line, their word, then ours. A grid of dropdowns for a
// set whose size we do not know would be worse.
function mapToText(m: Record<string, number>): string {
  return Object.entries(m)
    .sort((a, b) => a[1] - b[1] || a[0].localeCompare(b[0]))
    .map(([k, v]) => k + ' = ' + v)
    .join('\n')
}

function textToMap(t: string): Record<string, number> {
  const out: Record<string, number> = {}
  for (const line of t.split('\n')) {
    const at = line.lastIndexOf('=')
    if (at < 0) continue
    const key = line.slice(0, at).trim().toLowerCase()
    const n = Number(line.slice(at + 1).trim())
    if (key === '' || !Number.isInteger(n) || n < 1 || n > 5) continue
    out[key] = n
  }
  return out
}

export function RiskSources() {
  const [list, setList] = useState<RiskSource[] | null>(null)
  const [error, setError] = useState('')
  const [open, setOpen] = useState(false)
  const [kind, setKind] = useState('GENERIC_WEBHOOK')
  const [name, setName] = useState('')
  const [origin, setOrigin] = useState('')
  const [secret, setSecret] = useState('')
  const [busy, setBusy] = useState(false)
  const [created, setCreated] = useState<{ url: string; note: string } | null>(null)
  const [editing, setEditing] = useState<string | null>(null)
  const [likelihoodText, setLikelihoodText] = useState('')
  const [impactText, setImpactText] = useState('')
  // What a save said, per source: the console's count on the line and the
  // API's note behind the (i) beside it.
  const [outcome, setOutcome] = useState<Record<string, { said: string; note?: string }>>({})

  function load() {
    api
      .riskSources()
      .then((r) => {
        setList(r.riskSources)
      })
      .catch((e) => setError(describe(e)))
  }
  useEffect(load, [])

  async function create(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError('')
    try {
      const r = await api.createRiskSource({ kind, name, originLabel: origin || name, secret })
      setOpen(false)
      setName('')
      setOrigin('')
      setSecret('')
      // Shown until they navigate away, because the secret cannot be shown
      // again and the address is what they have to paste into the other
      // system. Losing it here means creating another source.
      setCreated({ url: r.webhookUrl, note: r.note })
      load()
    } catch (err) {
      setError(describe(err))
    } finally {
      setBusy(false)
    }
  }

  function startEditing(s: RiskSource) {
    setEditing(s.id)
    setLikelihoodText(mapToText(s.likelihoodMap))
    setImpactText(mapToText(s.impactMap))
  }

  async function saveScales(id: string) {
    setBusy(true)
    try {
      const r = await api.updateRiskSourceScales(id, textToMap(likelihoodText), textToMap(impactText))
      setOutcome((o) => ({ ...o, [id]: { said: r.replaced + ' risks placed again', note: r.note } }))
      setEditing(null)
      load()
    } catch (err) {
      setOutcome((o) => ({ ...o, [id]: { said: describe(err) } }))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="p1-page">
      <div className="p1-head">
        <div>
          <p className="muted">
            <a href="#/settings">Settings</a>
          </p>
          <h1>Import risks</h1>
        </div>
        <div className="p1-actions">
          <button
            type="button"
            className="button primary"
            disabled={!can('config.write')}
            title={can('config.write') ? undefined : refusalReason('config.write')}
            onClick={() => setOpen((v) => !v)}
          >
            Add a register
          </button>
        </div>
      </div>

      {error && <p className="note warn">{error}</p>}

      {created && (
        <div className="panel">
          <h2>Point the other system here</h2>
          <p className="mono" style={{ wordBreak: 'break-all' }}>
            POST {created.url}
          </p>
          {/* How to connect it is the API's sentence, kept word for word behind
              the (i); the line says the part that cannot wait. */}
          <p className="note warn sw-row">
            <span>Set it up in the other system now, the secret cannot be shown again</span>
            <ReasonTip label="How to connect the other system" text={created.note} />
          </p>
          <button type="button" className="button" onClick={() => setCreated(null)}>
            I have copied it
          </button>
        </div>
      )}

      {open && (
        <form className="panel wizard-form" onSubmit={(e) => void create(e)}>
          <h2>New register</h2>
          <div className="rs-kinds">
            {Object.entries(KIND_WORD).map(([k, w]) => (
              <button type="button" key={k} className={'chip' + (kind === k ? ' strong' : '')} onClick={() => setKind(k)}>
                {w}
              </button>
            ))}
          </div>
          <label>
            Name
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Zoho enterprise risk register" required />
          </label>
          <label>
            What to call these on the matrix
            <input value={origin} onChange={(e) => setOrigin(e.target.value)} placeholder="Zoho" />
          </label>
          <label>
            Secret
            <input
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              type="password"
              autoComplete="off"
              minLength={16}
              required
            />
          </label>
          <div className="p1-actions">
            <button type="submit" className="button primary" disabled={busy} data-action="tprm.risk-sources.a01">
              {busy ? 'Saving' : 'Save and show me the address'}
            </button>
            <button
              type="button"
              className="button ghost"
              onClick={() => {
                setOpen(false)
                setError('')
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {list && list.length === 0 && !open && (
        <div className="empty">
          <strong>No register is sending risks yet</strong>
          <p className="muted" style={{ margin: '4px 0 0' }}>
            Add one and its risks join your matrix, marked with where they came from
          </p>
        </div>
      )}

      {list && list.length > 0 && (
        <div className="p1-cards2">
          {list.map((s) => (
            <section className="p1-card2" key={s.id}>
              <h2>
                {s.name} <span className="chip">{KIND_WORD[s.kind] ?? s.kind}</span>{' '}
                {!s.enabled && <Status tone="warn">Paused</Status>}
              </h2>
              <p className="muted mono" style={{ wordBreak: 'break-all' }}>
                POST /v1/risk-inbound/{s.id}
              </p>
              <div className="std-meta">
                <span className="chip">{s.live} on the matrix</span>
                <span className={'chip' + (s.unplaced > 0 ? ' warn' : '')}>{s.unplaced} not placed</span>
                {s.withdrawn > 0 && <span className="chip">{s.withdrawn} withdrawn</span>}
                <span className="chip">shown as {s.originLabel}</span>
                {s.lastSeenAt && <span className="chip">last sent {s.lastSeenAt.slice(0, 16).replace('T', ' ')}</span>}
              </div>
              {s.lastError && <p className="note warn">{s.lastError}</p>}
              {/* The count is already in the chips above; this line says what to
                  do about it, and why they are off the matrix is behind the (i). */}
              {s.unplaced > 0 && (
                <p className="note warn sw-row">
                  <span>Add the missing level to their scale to place them</span>
                  <Toggletip label="Why some are not placed">
                    Kept and shown but not on the matrix, because the level each one used is not in the mapping. Nothing
                    was dropped. Adding the level places them, including the ones already sent.
                  </Toggletip>
                </p>
              )}
              {outcome[s.id] && (
                <p className="note ok sw-row">
                  <span>{outcome[s.id]?.said}</span>
                  <ReasonTip label="What placing again means" text={outcome[s.id]?.note} />
                </p>
              )}

              {editing === s.id ? (
                <div className="rs-scales">
                  <label>
                    Their likelihood, one per line, as <span className="mono">their word = 1 to 5</span>
                    <textarea rows={8} value={likelihoodText} onChange={(e) => setLikelihoodText(e.target.value)} />
                  </label>
                  <label>
                    Their impact
                    <textarea rows={8} value={impactText} onChange={(e) => setImpactText(e.target.value)} />
                  </label>
                  <div className="p1-actions">
                    <button
                      type="button"
                      className="button primary"
                      disabled={busy}
                      onClick={() => void saveScales(s.id)}
                      data-action="tprm.risk-sources.a02"
                    >
                      {busy ? 'Saving' : 'Save and place them again'}
                    </button>
                    <button type="button" className="button ghost" onClick={() => setEditing(null)}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="p1-actions">
                  <button
                    type="button"
                    className="button"
                    disabled={!can('config.write')}
                    title={can('config.write') ? undefined : refusalReason('config.write')}
                    onClick={() => startEditing(s)}
                  >
                    Their scale
                  </button>
                  <button
                    type="button"
                    className="button ghost"
                    disabled={busy || !can('config.write')}
                    onClick={() => void api.enableRiskSource(s.id, !s.enabled).then(load)}
                    data-action="tprm.risk-sources.a03"
                  >
                    {s.enabled ? 'Pause' : 'Resume'}
                  </button>
                  <a className="button ghost" href="#/risk">
                    See them on the matrix
                  </a>
                </div>
              )}
            </section>
          ))}
        </div>
      )}
    </div>
  )
}
